import FeatureLockScreen from '../../components/apple-ui/FeatureLockScreen';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import ReferralToast from '../../components/apple-ui/ReferralToast';
import { shouldShowExportReferral, markExportReferralShown, shouldShowFirstFeatureReferral, markFirstFeatureUsed, EXPORT_MSG, FIRST_FEATURE_MSG } from '../../utils/referralTriggers';
import { usePlan } from '@/contexts/PlanContext';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { MainLayoutApple, CardApple, ButtonApple, BadgeApple, GlobalContextBarApple } from '@/components/apple-ui';
import ActiveContextBadgesApple from '../../components/apple-ui/ActiveContextBadgesApple';
import ContextSelectorModal from '../../components/ContextSelectorModal';
import { useGlobalContext } from '@/contexts/GlobalContextProvider';
import {
  FileUp, FileText, Download, Loader2, Lock, CheckCircle, AlertTriangle, ArrowRight,
  BarChart3, FileCheck, Star, History, X, Search, BookOpen, SlidersHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import ExportFeedbackModal from '../../components/ExportFeedbackModal';
import '@/styles/apple-design-system.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_FILE_TYPES = ['.docx', '.pdf', '.xlsx', '.txt'];
const FILE_ACCEPT_STRING = SUPPORTED_FILE_TYPES.join(',');

const LANGUAGES = ['English', 'Hindi', 'Punjabi', 'Gujarati', 'Marathi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Odia', 'Assamese', 'Urdu'];

const TQAPageApple = ({ user, planInfo, onLogout }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const tqaAccess = useFeatureAccess('tqa');
  const { refreshPlanInfo } = usePlan();
  const { showContextModal, setShowContextModal, getContextIds } = useGlobalContext();

  // File state
  const [showExportFeedback, setShowExportFeedback] = useState(false);
  const [referralToast, setReferralToast] = useState(null);

  const triggerExportReferral = () => {
    if (shouldShowExportReferral()) {
      markExportReferralShown();
      setReferralToast({ message: EXPORT_MSG, buttons: 'dismiss' });
    }
  };
  const triggerFirstFeatureReferral = () => {
    if (shouldShowFirstFeatureReferral()) {
      markFirstFeatureUsed();
      setTimeout(() => setReferralToast(prev => prev || { message: FIRST_FEATURE_MSG, buttons: 'remind_later' }), 3000);
    }
  };

  const [sourceFile, setSourceFile] = useState(null);
  const [targetFile, setTargetFile] = useState(null);
  const [sourceLang, setSourceLang] = useState('English');
  const [targetLang, setTargetLang] = useState('Hindi');
  
  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [mqmCounts, setMqmCounts] = useState({ critical: null, major: null, minor: null, total: null });
  const [qualityTier, setQualityTier] = useState(null);
  const [overlayStage, setOverlayStage] = useState(0);
  const [reportReady, setReportReady] = useState(false);

  // Evaluation mode
  const [evaluationMode, setEvaluationMode] = useState('mt_review');

  // Context state
  const [customInstructions, setCustomInstructions] = useState('');
  const [glossaryCount, setGlossaryCount] = useState(0);
  const [appliedContext, setAppliedContext] = useState(null);

  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const sourceInputRef = useRef(null);
  const targetInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/mtpe/glossary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { count: 0 })
      .then(d => setGlossaryCount(d.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!analyzing) { setOverlayStage(0); return; }
    const iv = setInterval(() => {
      setOverlayStage(prev => Math.min(prev + 1, 3));
    }, 2000);
    return () => clearInterval(iv);
  }, [analyzing]);

  // Feature gating — must be after all hooks
  if (!tqaAccess.hasAccess) {
    return <FeatureLockScreen feature="tqa" requiredPlan={tqaAccess.upgradeTo || 'professional'} currentPlan={tqaAccess.currentPlan} />;
  }

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tqa/history`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      setHistory(data.reports || []);
    } catch (error) { console.error('Failed to load history:', error); }
    finally { setLoadingHistory(false); }
  };

  const isFileSupported = (filename) => {
    const ext = '.' + filename.toLowerCase().split('.').pop();
    return SUPPORTED_FILE_TYPES.includes(ext);
  };

  const handleSourceFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!isFileSupported(file.name)) { toast.error(`Supported formats: ${SUPPORTED_FILE_TYPES.join(', ')}`); return; }
      setSourceFile(file);
      setReportReady(false);
    }
  };

  const handleTargetFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!isFileSupported(file.name)) { toast.error(`Supported formats: ${SUPPORTED_FILE_TYPES.join(', ')}`); return; }
      setTargetFile(file);
      setReportReady(false);
    }
  };

  const handleAnalyze = async () => {
    if (!sourceFile || !targetFile) { toast.error('Please upload both source and target files'); return; }
    setMqmCounts({ critical: null, major: null, minor: null, total: null });
    setQualityTier(null);
    setAnalyzing(true);
    setReportReady(false);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('source_file', sourceFile);
      formData.append('target_file', targetFile);
      formData.append('source_lang', sourceLang);
      formData.append('target_lang', targetLang);
      if (customInstructions.trim()) formData.append('custom_instructions', customInstructions.trim());
      formData.append('evaluation_mode', evaluationMode);
      const contextIds = getContextIds();
      console.log('[TQA] contextIds being sent:', contextIds);
      if (contextIds.instructionId) formData.append('context_instruction_id', contextIds.instructionId);
      if (contextIds.glossaryId) formData.append('context_glossary_id', contextIds.glossaryId);
      if (contextIds.projectId) formData.append('context_project_id', contextIds.projectId);

      const response = await fetch(`${API_URL}/api/tqa/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const contentType = response.headers.get('content-type') || '';
      
      if (response.status >= 400 || !contentType.includes('application/pdf')) {
        let errorMessage = 'Analysis failed. Please try again.';
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            if (typeof errorData.detail === 'object' && errorData.detail.error === 'insufficient_credits') {
              errorMessage = `Insufficient balance. TQA charges 50% of target word count. Your balance: ₹${errorData.detail.balance || 0}.`;
            } else {
              errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
            }
          }
        } catch (parseError) {
          if (response.status === 402) errorMessage = 'Insufficient balance. TQA charges 50% of target word count per report.';
          else if (response.status === 403) errorMessage = 'Access denied. Please check your subscription.';
        }
        toast.error(errorMessage);
        return;
      }

      // Read MQM counts from response headers
      const critical = parseInt(response.headers.get('X-TQA-Critical') || '0', 10);
      const major = parseInt(response.headers.get('X-TQA-Major') || '0', 10);
      const minor = parseInt(response.headers.get('X-TQA-Minor') || '0', 10);
      const total = parseInt(response.headers.get('X-TQA-Total') || '0', 10);
      const tier = response.headers.get('X-TQA-Tier') || '';
      const appliedGlossaryCount = parseInt(response.headers.get('X-TQA-Glossary-Count') || '0', 10);
      const instructionsApplied = response.headers.get('X-TQA-Instructions-Applied') === '1';
      setAppliedContext({ glossaryCount: appliedGlossaryCount, instructionsApplied });

      // Success - create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TQA_Report_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMqmCounts({ critical, major, minor, total });
      setQualityTier(tier);

      setReportReady(true);
      toast.success('TQA Report generated and downloaded!'); setShowExportFeedback(true); setTimeout(() => refreshPlanInfo(), 1500);
      triggerExportReferral();
      triggerFirstFeatureReferral();
      loadHistory();
    } catch (error) { toast.error('Analysis failed. Please try again.'); }
    finally { setAnalyzing(false); }
  };

  const resetFiles = () => {
    setSourceFile(null);
    setTargetFile(null);
    setReportReady(false);
    setMqmCounts({ critical: null, major: null, minor: null, total: null });
    setQualityTier(null);
    setAppliedContext(null);
    if (sourceInputRef.current) sourceInputRef.current.value = '';
    if (targetInputRef.current) targetInputRef.current.value = '';
  };

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      {analyzing && (() => {
        const tqaProgress = Math.max(5, (overlayStage / 3) * 100);
        const tqaStageIdx = overlayStage;
        const tqaDescs = ['Parsing document structure and extracting segments', 'Running deterministic quality checks', 'Analyzing translation errors across all segments', 'Compiling quality report'];
        const tqaStages = [{ label: 'Parsing Document', Icon: FileText }, { label: 'Quality Checks', Icon: Search }, { label: 'Analyzing Errors', Icon: BarChart3 }, { label: 'Report Ready', Icon: FileCheck }];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: isDarkMode ? 'rgba(10,15,26,0.92)' : 'rgba(255,255,255,0.95)' }}>
            <style>{`@keyframes tqa-overlay-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } } @keyframes tqa-bar-shimmer { 0% { transform: translateX(-200%); } 100% { transform: translateX(400%); } }`}</style>
            <div style={{ background: isDarkMode ? '#111827' : '#ffffff', borderRadius: 16, padding: '40px 48px', width: '100%', maxWidth: 520, boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.12)', border: isDarkMode ? '1px solid #1e293b' : '1px solid #e5e7eb', animation: 'tqa-overlay-in 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 44, height: 44, background: '#000', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: isDarkMode ? '0 0 0 2px #ffffff' : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)' }}>
                    <img src="/logo-marketing.png" alt="t09n.com" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0, display: 'block' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: 18, color: isDarkMode ? '#ffffff' : '#000000', letterSpacing: '-0.3px', lineHeight: 1.1 }}>t09n<span style={{ color: isDarkMode ? '#ffffff' : '#333333', fontWeight: 700 }}>.com</span></span>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', marginTop: 3, fontSize: 11, color: isDarkMode ? '#ffffff' : '#000000', fontWeight: 700, fontStyle: 'oblique', lineHeight: 1.2, letterSpacing: '0.1px' }}>The Operating System For Translators</span>
                  </div>
                </div>
              </div>
              <h2 style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#111827', margin: '0 0 6px' }}>Checking Translation Quality</h2>
              <p style={{ textAlign: 'center', fontSize: 13, color: isDarkMode ? '#64748b' : '#6b7280', margin: '0 0 28px' }}>{tqaDescs[tqaStageIdx]}</p>
              <div style={{ position: 'relative', height: 6, background: isDarkMode ? '#1e293b' : '#f3f4f6', borderRadius: 6, marginBottom: 28, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${tqaProgress}%`, background: 'linear-gradient(90deg, #1d4ed8, #2563eb, #3b82f6)', borderRadius: 6, transition: 'width 1.8s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', animation: 'tqa-bar-shimmer 1.5s ease-in-out infinite' }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {tqaStages.map(({ label, Icon }, idx) => {
                  const done = tqaStageIdx > idx;
                  const active = tqaStageIdx === idx;
                  return (
                    <div key={label} style={{ flex: 1, padding: '10px 6px', borderRadius: 12, textAlign: 'center', background: done ? (isDarkMode ? '#14532d' : '#f0fdf4') : active ? (isDarkMode ? '#1e293b' : '#f0f4ff') : (isDarkMode ? '#1e293b' : '#f9fafb'), border: `1px solid ${done ? (isDarkMode ? '#16a34a' : '#86efac') : active ? (isDarkMode ? '#60a5fa' : '#93c5fd') : (isDarkMode ? '#1e293b' : '#e5e7eb')}`, transition: 'all 0.4s ease' }}>
                      <Icon size={16} style={{ margin: '0 auto 6px', color: done ? (isDarkMode ? '#4ade80' : '#16a34a') : active ? (isDarkMode ? '#60a5fa' : '#2563eb') : (isDarkMode ? '#475569' : '#9ca3af'), display: 'block', transition: 'color 0.4s' }} />
                      <p style={{ fontSize: 11, fontWeight: 600, color: done ? (isDarkMode ? '#4ade80' : '#15803d') : active ? (isDarkMode ? '#60a5fa' : '#1d4ed8') : (isDarkMode ? '#475569' : '#9ca3af'), lineHeight: 1.3, margin: 0 }}>{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      <div className="apple-content" data-testid="apple-tqa-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-purple-100 rounded-2xl">
              <BarChart3 className="text-purple-600" size={28} />
            </div>
            <div>
              <h1 className="apple-content-title mb-0">Translation Quality Assessment</h1>
              <p className="apple-content-subtitle">Upload source and target documents to analyze translation quality</p>
            </div>
          </div>
          <ButtonApple variant="secondary" onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }} icon={History} data-testid="tqa-history-btn">
            History
          </ButtonApple>
        </div>

        {/* Pricing Badge */}
        <div className={`mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-2xl ${isDarkMode ? 'bg-purple-900/30 border border-purple-800' : 'bg-purple-50 border border-purple-100'}`}>
          <span className="text-lg font-bold text-purple-700">50%</span>
          <span className="text-sm text-purple-600">of target word count • Deducted from word quota</span>
        </div>

        {/* Global Context Bar */}
        <GlobalContextBarApple onOpenModal={() => setShowContextModal(true)} isDarkMode={isDarkMode} />
        <ContextSelectorModal isOpen={showContextModal} onClose={() => setShowContextModal(false)} variant="apple" />

        {/* Evaluation Mode Selector */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <SlidersHorizontal size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Evaluation Mode</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                id: 'mt_review',
                title: 'MT Post-Edit Review',
                badge: 'Recommended for machine-translated content',
                badgeColor: isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700',
                description: 'Designed for documents translated using translation engines. Evaluates whether meaning is accurately preserved and terminology is appropriate. Minor stylistic variations are acceptable. Use this when checking output from translation tools before post-editing or client delivery.',
                whenToUse: ['Documents translated with CAT tools', 'Machine-translated drafts', 'Pre-post-editing quality check'],
              },
              {
                id: 'human_qa',
                title: 'Human Translation QA',
                badge: 'For professionally translated documents',
                badgeColor: isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-700',
                description: 'Applies strict professional translation standards used by agencies and enterprise clients. Evaluates accuracy, fluency, terminology, style, and locale conventions at the highest level. Use this when reviewing work submitted by human translators for final delivery approval.',
                whenToUse: ['Freelancer submissions', 'Agency deliverables', 'Final client delivery review'],
              },
            ].map((mode) => {
              const selected = evaluationMode === mode.id;
              return (
                <button
                  key={mode.id}
                  onClick={() => setEvaluationMode(mode.id)}
                  disabled={analyzing}
                  style={{
                    textAlign: 'left',
                    padding: '20px',
                    borderRadius: '16px',
                    border: `2px solid ${selected ? '#2563eb' : (isDarkMode ? '#374151' : '#e5e7eb')}`,
                    background: selected
                      ? (isDarkMode ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.04)')
                      : (isDarkMode ? '#1f2937' : '#ffffff'),
                    cursor: analyzing ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    opacity: analyzing ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
                        border: `2px solid ${selected ? '#2563eb' : (isDarkMode ? '#4b5563' : '#d1d5db')}`,
                        background: selected ? '#2563eb' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }} />}
                      </div>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: selected ? (isDarkMode ? '#93c5fd' : '#1d4ed8') : (isDarkMode ? '#f9fafb' : '#111827') }}>
                        {mode.title}
                      </span>
                    </div>
                  </div>
                  <span style={{ display: 'inline-block', fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px', marginBottom: '10px' }} className={mode.badgeColor}>
                    {mode.badge}
                  </span>
                  <p style={{ fontSize: '13px', color: isDarkMode ? '#9ca3af' : '#6b7280', lineHeight: '1.6', margin: '0 0 12px' }}>
                    {mode.description}
                  </p>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {mode.whenToUse.map((item) => (
                      <li key={item} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: isDarkMode ? '#6ee7b7' : '#16a34a', fontWeight: '500' }}>
                        <CheckCircle size={12} style={{ flexShrink: 0 }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        </div>

        {/* File Upload Section */}
        <CardApple className="mb-6">
          <CardApple.Body>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-center">
              {/* Source File */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={18} className="text-blue-500" />
                  <span className="font-semibold text-gray-900">Source Document</span>
                </div>
                <select value={sourceLang} onChange={(e) => setSourceLang(e.target.value)} disabled={analyzing} className="apple-input apple-select" data-testid="tqa-source-lang">
                  {LANGUAGES.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
                <div 
                  className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${sourceFile ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}
                  onClick={() => sourceInputRef.current?.click()}
                  data-testid="tqa-source-dropzone"
                >
                  <input type="file" accept={FILE_ACCEPT_STRING} onChange={handleSourceFileChange} ref={sourceInputRef} className="hidden" data-testid="tqa-source-input" />
                  {sourceFile ? (
                    <div className="space-y-2">
                      <FileCheck size={40} className="mx-auto text-green-500" />
                      <p className="font-medium text-gray-900 truncate">{sourceFile.name}</p>
                      <p className="text-sm text-gray-500">{(sourceFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <>
                      <FileUp size={40} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 font-medium">Drop file here or click</p>
                      <p className="text-xs text-gray-400 mt-1">DOCX, PDF, XLSX, TXT</p>
                    </>
                  )}
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRight size={28} className="text-gray-300" />
              </div>

              {/* Target File */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={18} className="text-green-500" />
                  <span className="font-semibold text-gray-900">Target Document</span>
                </div>
                <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} disabled={analyzing} className="apple-input apple-select" data-testid="tqa-target-lang">
                  {LANGUAGES.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
                <div 
                  className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${targetFile ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-green-300 hover:bg-green-50'}`}
                  onClick={() => targetInputRef.current?.click()}
                  data-testid="tqa-target-dropzone"
                >
                  <input type="file" accept={FILE_ACCEPT_STRING} onChange={handleTargetFileChange} ref={targetInputRef} className="hidden" data-testid="tqa-target-input" />
                  {targetFile ? (
                    <div className="space-y-2">
                      <FileCheck size={40} className="mx-auto text-green-500" />
                      <p className="font-medium text-gray-900 truncate">{targetFile.name}</p>
                      <p className="text-sm text-gray-500">{(targetFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <>
                      <FileUp size={40} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 font-medium">Drop file here or click</p>
                      <p className="text-xs text-gray-400 mt-1">DOCX, PDF, XLSX, TXT</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Analysis Context */}
            <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal size={15} className={isDarkMode ? 'text-purple-400' : 'text-purple-500'} />
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Analysis Context</span>
                </div>
                {glossaryCount > 0 && (
                  <button
                    onClick={() => navigate('/glossary')}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-medium transition-colors ${isDarkMode ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                  >
                    <BookOpen size={12} />
                    {glossaryCount} glossary {glossaryCount === 1 ? 'term' : 'terms'} active
                  </button>
                )}
                {glossaryCount === 0 && (
                  <button
                    onClick={() => navigate('/glossary')}
                    className={`text-xs ${isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                  >
                    + Add glossary terms
                  </button>
                )}
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Domain notes <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>(optional — applied to this run only)</span>
                </label>
                <textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  disabled={analyzing}
                  placeholder="e.g. Finance document — use standard banking terminology. Formal register required."
                  rows={2}
                  className={`w-full rounded-xl px-3 py-2.5 text-sm resize-none transition-colors ${isDarkMode ? 'bg-gray-800 border border-gray-700 text-gray-200 placeholder-gray-600 focus:border-purple-500' : 'bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 focus:border-purple-400'} outline-none focus:ring-2 focus:ring-purple-400/20`}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-gray-100">
              <ButtonApple onClick={handleAnalyze} disabled={!sourceFile || !targetFile || analyzing} icon={analyzing ? Loader2 : BarChart3} data-testid="tqa-analyze-btn">
                {analyzing ? 'Analyzing Quality...' : 'Analyze Quality'}
              </ButtonApple>
              {(sourceFile || targetFile) && (
                <ButtonApple variant="secondary" onClick={resetFiles} data-testid="tqa-reset-btn">Reset Files</ButtonApple>
              )}
            </div>
          </CardApple.Body>
        </CardApple>

        {/* MQM Error Count Cards — appear during analysis and after */}
        {(analyzing || (reportReady && mqmCounts.total !== null)) && (
          <CardApple className="mb-6">
            <CardApple.Body>
              <style>{`@keyframes tqa-shimmer { 0% { transform: translateX(-200%); } 100% { transform: translateX(400%); } } @keyframes tqa-score-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                {analyzing ? 'Analyzing…' : 'Error Summary'}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {[
                  { key: 'critical', label: 'Critical', color: '#dc2626', bg: '#fef2f2' },
                  { key: 'major',    label: 'Major',    color: '#d97706', bg: '#fffbeb' },
                  { key: 'minor',    label: 'Minor',    color: '#2563eb', bg: '#eff6ff' },
                  { key: 'total',    label: 'Total',    color: '#374151', bg: '#f8fafc' },
                ].map(({ key, label, color, bg }) => {
                  const val = mqmCounts[key];
                  return (
                    <div key={key} style={{ padding: 16, borderRadius: 12, background: val === null ? (isDarkMode ? '#1e293b' : '#f3f4f6') : (isDarkMode ? '#1e293b' : bg), border: `1px solid ${val === null ? (isDarkMode ? '#334155' : '#e5e7eb') : color + '40'}`, transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', overflow: 'hidden' }}>
                      {val === null && (
                        <div style={{ position: 'absolute', top: 0, bottom: 0, width: '60%', background: isDarkMode ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)' : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)', animation: 'tqa-shimmer 1.5s ease-in-out infinite' }} />
                      )}
                      <p style={{ fontSize: 11, color: isDarkMode ? '#64748b' : '#6b7280', marginBottom: 6 }}>{label}</p>
                      <p style={{ fontSize: 28, fontWeight: 700, color: val === null ? (isDarkMode ? '#334155' : '#d1d5db') : color, transition: 'color 0.4s' }}>
                        {val === null ? '—' : val}
                      </p>
                      <p style={{ fontSize: 11, color: isDarkMode ? '#475569' : '#9ca3af' }}>errors</p>
                    </div>
                  );
                })}
              </div>
              {qualityTier && (() => {
                const tierColors = {
                  'Fit for Publication': '#16a34a',
                  'Acceptable':          '#2563eb',
                  'Needs Revision':      '#d97706',
                  'Unacceptable':        '#dc2626',
                };
                const tc = tierColors[qualityTier] || '#6b7280';
                return (
                  <div style={{ padding: '12px 16px', background: isDarkMode ? '#0f172a' : '#f0f4ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', animation: 'tqa-score-in 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#374151' }}>Quality Tier</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: tc }}>{qualityTier}</span>
                  </div>
                );
              })()}
            </CardApple.Body>
          </CardApple>
        )}

        {/* Context Applied Indicator */}
        {reportReady && appliedContext !== null && (
          <div style={{
            marginBottom: 16,
            padding: '8px 14px',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: (appliedContext.glossaryCount > 0 || appliedContext.instructionsApplied)
              ? (isDarkMode ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.06)')
              : (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
            border: `1px solid ${(appliedContext.glossaryCount > 0 || appliedContext.instructionsApplied) ? (isDarkMode ? 'rgba(37,99,235,0.3)' : 'rgba(37,99,235,0.2)') : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)')}`,
          }}>
            <BookOpen size={13} style={{ color: (appliedContext.glossaryCount > 0 || appliedContext.instructionsApplied) ? '#2563eb' : (isDarkMode ? '#6b7280' : '#9ca3af'), flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: (appliedContext.glossaryCount > 0 || appliedContext.instructionsApplied) ? (isDarkMode ? '#93c5fd' : '#1d4ed8') : (isDarkMode ? '#6b7280' : '#9ca3af') }}>
              {(appliedContext.glossaryCount > 0 || appliedContext.instructionsApplied)
                ? `Analysis considered: ${[
                    appliedContext.glossaryCount > 0 && `${appliedContext.glossaryCount} glossary ${appliedContext.glossaryCount === 1 ? 'term' : 'terms'}`,
                    appliedContext.instructionsApplied && 'project instructions applied'
                  ].filter(Boolean).join(' · ')}`
                : 'No context selected — used default evaluation settings'}
            </span>
          </div>
        )}

        {/* Success Message */}
        {reportReady && (
          <div className="p-5 mb-6 bg-green-50 border border-green-200 rounded-2xl" data-testid="tqa-success">
            <div className="flex items-center gap-3">
              <CheckCircle size={24} className="text-green-500" />
              <div>
                <p className="font-semibold text-green-800">Report Generated!</p>
                <p className="text-sm text-green-700">Your TQA report has been downloaded. Check your downloads folder.</p>
              </div>
            </div>
          </div>
        )}

        {/* What's Included */}
        <CardApple>
          <CardApple.Header>
            <CardApple.Title>What's Included in the Report</CardApple.Title>
          </CardApple.Header>
          <CardApple.Body>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Star, color: 'text-yellow-500', title: 'Quality Scores', desc: '5 categories rated 1-5' },
                { icon: AlertTriangle, color: 'text-red-500', title: 'Error Analysis', desc: 'Critical, Major, Minor issues' },
                { icon: FileText, color: 'text-blue-500', title: 'PDF Report', desc: 'Professional document' },
                { icon: CheckCircle, color: 'text-green-500', title: 'Recommendations', desc: 'Improvement tips' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-2xl">
                  <item.icon size={20} className={`${item.color} flex-shrink-0 mt-0.5`} />
                  <div>
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardApple.Body>
        </CardApple>

        {/* History Panel */}
        {showHistory && (
          <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30" onClick={() => setShowHistory(false)}>
            <div className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="tqa-history-panel">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-5 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-lg">Report History</h3>
                <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><X size={20} /></button>
              </div>
              <div className="p-6">
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8"><Loader2 size={24} className="animate-spin text-purple-600" /></div>
                ) : history.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No reports yet</p>
                ) : (
                  <div className="space-y-4">
                    {history.map((report) => (
                      <div key={report.report_id} className="p-4 bg-gray-50 rounded-2xl">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <span className="truncate">{report.source_filename}</span>
                          <ArrowRight size={14} />
                          <span className="truncate">{report.target_filename}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="font-semibold text-purple-600">{report.quality_tier || 'N/A'}</span>
                          <span className="text-red-500">{report.critical_count ?? 0} crit</span>
                          <span className="text-amber-500">{report.major_count ?? 0} maj</span>
                          <span className="text-gray-400 text-xs">{new Date(report.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {showExportFeedback && (
        <ExportFeedbackModal
          feature="tqa"
          onClose={() => setShowExportFeedback(false)}
        />
      )}
      <ReferralToast
        toast={referralToast}
        onDismiss={() => setReferralToast(null)}
      />
    </MainLayoutApple>
  );
};

export default TQAPageApple;
